import { useEffect, useState } from 'react'
import { api, type Stufe, type Zugang, type Zugangsbild } from '../api'
import { anzahl } from '../format'
import { Tipp } from '../komponenten/Hinweis'

/* Zugänge im eigenen Haus.
 *
 * Was hier steht, ist **nicht** die Rangordnung — die steht in der
 * Datenbank. Diese Ansicht zeigt nur, was die API zurückgibt: Wen ich
 * sehe, welche Stufen ich anlegen darf, welche Standorte ich zuteilen
 * kann. Ein Betriebsleiter bekommt hier also von allein nur
 * „Mitarbeiter" zur Auswahl, ohne dass ein einziges `if` darüber
 * entscheidet.
 *
 * Das Passwort kommt hier nie vor. Wer einen Zugang anlegt, bekommt
 * einen **Link** — und soll das Passwort des anderen gar nicht kennen.
 */

/* Der Link kommt **vom Server**, nicht aus `window.location`.
 *
 * Der Browser weiß nur, unter welcher Adresse er gerade guckt — beim
 * Betreiber ist das `localhost`. Ein so gebauter Link zeigt beim Kunden
 * auf dessen eigenen Rechner: Er sieht richtig aus und führt ins Leere.
 * Welche Adresse von außen gilt, weiß nur der Server (`GASTRO_BASIS_URL`).
 */
function Link({ voll, tage, fuer }:
              { voll: string; tage: number; fuer: string }) {
  const [kopiert, setKopiert] = useState(false)

  return (
    <div className="einladung">
      <b>Einladung für {fuer}</b>
      <p className="dezent">
        Diesen Link weitergeben. Er gilt <b>{tage} Tage</b> und genau
        <b> einmal</b>; danach hat der Zugang sein eigenes Passwort, das
        hier niemand kennt.
      </p>
      <div className="knopfreihe">
        <input className="linkfeld" readOnly value={voll}
               onFocus={(e) => e.currentTarget.select()} />
        <button type="button" onClick={() => {
          void navigator.clipboard?.writeText(voll)
            .then(() => setKopiert(true)).catch(() => undefined)
        }}>{kopiert ? 'Kopiert' : 'Kopieren'}</button>
      </div>
    </div>
  )
}

function Anlegen({ bild, fertig }:
                 { bild: Zugangsbild; fertig: () => void }) {
  const [offen, setOffen] = useState(false)
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [rolle, setRolle] = useState(bild.stufen[0]?.rolle ?? '')
  const [orte, setOrte] = useState<string[]>([])
  const [fehler, setFehler] = useState<string | null>(null)
  const [link, setLink] = useState<{ voll: string; tage: number; fuer: string } | null>(null)

  const stufe: Stufe | undefined = bild.stufen.find((s) => s.rolle === rolle)
  const brauchtStandort = stufe && stufe.betriebe !== 'ALLE'
  const nurEiner = stufe?.betriebe === 'GENAU_EINER'

  const umschalten = (id: string) => {
    if (nurEiner) { setOrte([id]); return }
    setOrte((a) => a.includes(id) ? a.filter((x) => x !== id) : [...a, id])
  }

  const senden = async (e: React.FormEvent) => {
    e.preventDefault()
    setFehler(null)
    try {
      const a = await api.haus.anlegen(email.trim(), name.trim(), rolle, orte)
      setLink({ voll: a.link, tage: a.tage, fuer: name.trim() })
      setEmail(''); setName(''); setOrte([])
      fertig()
    } catch (err) {
      setFehler(err instanceof Error ? err.message : String(err))
    }
  }

  if (bild.stufen.length === 0) return null

  return (
    <div className="karte">
      <div className="karte-kopf">
        <h3>Zugang anlegen</h3>
        <button className="verweis" onClick={() => setOffen(!offen)}>
          {offen ? 'zuklappen' : 'aufklappen'}
        </button>
      </div>

      {link && <Link {...link} />}

      {offen && (
        <form onSubmit={senden} className="zugangsformular">
          <label htmlFor="zn">Name</label>
          <input id="zn" value={name} required
                 onChange={(e) => setName(e.target.value)} />

          <label htmlFor="ze">E-Mail</label>
          <input id="ze" type="email" value={email} required
                 onChange={(e) => setEmail(e.target.value)} />

          <label htmlFor="zr">Stufe</label>
          <select id="zr" value={rolle}
                  onChange={(e) => { setRolle(e.target.value); setOrte([]) }}>
            {bild.stufen.map((s) => (
              <option key={s.rolle} value={s.rolle}>{s.bezeichnung}</option>
            ))}
          </select>

          {brauchtStandort && (
            <>
              <label>{nurEiner ? 'Standort' : 'Standorte'}</label>
              <div className="betriebswahl">
                {bild.betriebe.map((b) => (
                  <label key={b.id} className="kaestchen">
                    <input type={nurEiner ? 'radio' : 'checkbox'}
                           name="betrieb"
                           checked={orte.includes(b.id)}
                           onChange={() => umschalten(b.id)} />
                    {b.name}
                  </label>
                ))}
              </div>
              <p className="dezent">
                {nurEiner
                  ? 'Ein Mitarbeiter gehört zu genau einem Standort — und ist auch nur dort sichtbar.'
                  : 'Er sieht die Zahlen dieser Standorte. Alles andere bleibt ihm verborgen, auch ohne Filter.'}
              </p>
            </>
          )}

          {fehler && <div className="fehler" role="alert">{fehler}</div>}

          <div className="knopfreihe">
            <button type="submit"
                    disabled={!name.trim() || !email.trim()
                              || (brauchtStandort && orte.length === 0)}>
              Anlegen und einladen
            </button>
          </div>
        </form>
      )}
    </div>
  )
}

export function Zugaenge() {
  const [bild, setBild] = useState<Zugangsbild | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const [link, setLink] = useState<{ voll: string; tage: number; fuer: string } | null>(null)

  const laden = () => {
    api.haus.zugaenge().then(setBild).catch((e) => setFehler(e.message))
  }
  useEffect(laden, [])

  const schalten = async (z: Zugang) => {
    setFehler(null)
    try {
      await api.haus.schalten(z.id, !z.aktiv)
      laden()
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e))
    }
  }

  const erneuern = async (z: Zugang) => {
    setFehler(null)
    try {
      const a = await api.haus.einladungNeu(z.id)
      setLink({ voll: a.link, tage: a.tage, fuer: z.name })
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e))
    }
  }

  if (fehler && !bild) return <div className="fehler">{fehler}</div>
  if (!bild) return <div className="laedt">Wird geladen …</div>

  const namen = new Map(bild.betriebe.map((b) => [b.id, b.name]))

  return (
    <div className="backoffice">
      {fehler && <div className="fehler" role="alert">{fehler}</div>}

      <Anlegen bild={bild} fertig={laden} />
      {link && <div className="karte"><Link {...link} /></div>}

      <div className="karte">
        <div className="karte-kopf">
          <h3>Zugänge</h3>
          <span className="dezent">{anzahl(bild.zugaenge.length)}</span>
        </div>

        <p className="dezent">
          Hier stehen nur Zugänge <b>unter</b> Ihrer Stufe und nur aus
          Standorten, die Sie selbst sehen. Gleichgestellte und alles
          darüber verwaltet, wer darüber steht.
        </p>

        {bild.zugaenge.length === 0 ? (
          <p className="dezent">Noch niemand.</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Name</th><th>Stufe</th><th>Standorte</th>
                <th>Zuletzt da</th><th>Zustand</th><th></th>
              </tr>
            </thead>
            <tbody>
              {bild.zugaenge.map((z) => (
                <tr key={z.id} className={z.aktiv ? undefined : 'still'}>
                  <td>
                    <b>{z.name}</b>
                    <div className="dezent">{z.email}</div>
                  </td>
                  <td>{z.bezeichnung}</td>
                  <td className="dezent">
                    {z.betriebe.length === 0
                      ? 'alle'
                      : z.betriebe.map((b) => namen.get(b) ?? '—').join(', ')}
                  </td>
                  <td className="dezent zahl">
                    {z.letzte_anmeldung
                      ? z.letzte_anmeldung.slice(0, 10)
                      : '—'}
                  </td>
                  <td>
                    {!z.eingerichtet && (
                      <Tipp text="Eingeladen, aber noch ohne Passwort" fokus>
                        <span className="zustand">eingeladen</span>
                      </Tipp>
                    )}
                    {z.eingerichtet && !z.aktiv && (
                      <span className="zustand">abgeschaltet</span>
                    )}
                  </td>
                  <td className="r">
                    {!z.eingerichtet && (
                      <button className="verweis"
                              onClick={() => void erneuern(z)}>
                        neuer Link
                      </button>
                    )}
                    <button className="verweis"
                            onClick={() => void schalten(z)}>
                      {z.aktiv ? 'abschalten' : 'einschalten'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
